// Landing page for Share Visuals
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const CountUp: React.FC<{ end: number, duration?: number, suffix?: string }> = ({ end, duration = 2000, suffix = "" }) => {
    const [count, setCount] = useState(0);
    const elementRef = useRef<HTMLSpanElement>(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !hasAnimated.current) {
                hasAnimated.current = true;
                let startTimestamp: number | null = null;
                const step = (timestamp: number) => {
                    if (!startTimestamp) startTimestamp = timestamp;
                    const progress = Math.min((timestamp - startTimestamp) / duration, 1);
                    // Easing function: easeOutExpo
                    const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
                    setCount(Math.floor(easeProgress * end));
                    if (progress < 1) {
                        window.requestAnimationFrame(step);
                    }
                };
                window.requestAnimationFrame(step);
            }
        }, { threshold: 0.1 });

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        return () => observer.disconnect();
    }, [end, duration]);

    return <span ref={elementRef}>{count}{suffix}</span>;
};



const Landing: React.FC = () => {
    const { session } = useAuthContext();
    const [isLifetime, setIsLifetime] = useState(true);

    const features = [
        {
            icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
            title: 'Quote Art Cards',
            desc: 'Turn your best thoughts into scroll-stopping visual quotes. Perfect for Twitter/X and Instagram.'
        },
        {
            icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
            title: 'Stats Cards',
            desc: 'Share your metrics beautifully. Uptime, MRR, users — make your numbers look stunning.'
        },
        {
            icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
            title: 'SaaS Update Cards',
            desc: 'Document your BuildInPublic journey with beautiful progress cards your audience will love.'
        },
        {
            icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
            title: 'Premium Typography',
            desc: '50+ premium fonts optimized for social media. Your content deserves to look professional.'
        },
        {
            icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
            title: 'Instant Ship',
            desc: 'Download in seconds. PNG ready for any platform. No quality loss. Ship and go.'
        },
        {
            icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>,
            title: 'Brand Consistency',
            desc: 'Save your colors, fonts and style. Every post looks like you, every time.'
        },
    ];

    const steps = [
        { num: '01', title: 'Write Your Content', desc: 'Add your quote, stats or SaaS update. Or let AI generate it for you.' },
        { num: '02', title: 'Pick Your Style', desc: 'Choose from our designer templates or create your own custom look.' },
        { num: '03', title: 'Ship It', desc: 'Download your stunning visual instantly. Ready for any social platform.' },
    ];

    const testimonials = [
        {
            name: 'Corebit Systems',
            handle: '@CorebitSystems',
            text: "This is exactly what builders need. 🚀 Bulk generation isn't just a feature — it's a force multiplier for founders who understand that content velocity matters in building an audience. What impresses me technically: CSV upload + batch processing — smart architecture that separates data from design.",
            url: 'https://x.com/CorebitSystems/status/2029904329692369207?s=20',
            img: 'CS'
        },
        {
            name: 'Sadi Moodi',
            handle: '@MoodiSadi',
            text: "Love the focus on helping founders create SaaS journey content! Quick tip: I've found that adding a \"before/after\" metric to demo videos boosts engagement 3x. Also, consider adding a simple waitlist form - even 50 signups in week 1 validates demand.",
            url: 'https://x.com/MoodiSadi/status/2029927130566643946?s=20',
            img: 'SM'
        },
        {
            name: 'Henkjan',
            handle: '@henkjan',
            text: 'Founders ship faster is real value.',
            url: 'https://x.com/henkjan/status/2029788840219775237?s=20',
            img: 'HJ'
        },
        {
            name: 'Millionaire before 20',
            handle: '@millionaireb420',
            text: 'ngl at all.. as a person who does create content this side.. i find this useful mate',
            url: 'https://x.com/millionaireb420/status/2029934596763308445?s=20',
            img: 'MB'
        },
        {
            name: 'Factory Dev | $FDEV',
            handle: '@factory_fdev',
            text: 'Perfect for people building after their day job.',
            url: 'https://x.com/factory_fdev/status/2029943122017538399?s=20',
            img: 'FD'
        }
    ];

    return (
        <div className="min-h-screen bg-[#0F172A] text-white overflow-x-hidden selection:bg-primary-500/30 pt-20">
            <SEO />
            {/* --- Announcement Bar --- */}
            <div className="bg-[linear-gradient(90deg,#7C3AED,#06B6D4)] py-3 px-4 text-center relative z-50 overflow-hidden">
                <div className="absolute inset-0 bg-white/10 animate-pulse" />
                <p className="text-sm font-black tracking-wide relative flex items-center justify-center gap-2 text-white">
                    <span className="flex h-2 w-2 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                    </span>
                    ✨ New: Batch CSV generation now available in Pro & Lifetime
                </p>
            </div>

            {/* --- Hero Section --- */}
            <section className="relative pt-12 pb-16 md:pt-20 md:pb-24 px-4 overflow-hidden">
                {/* Background Decor */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1400px] h-full pointer-events-none -z-10">
                    <div className="absolute top-[-200px] left-[-100px] w-[600px] h-[600px] bg-primary-600/20 rounded-full blur-[120px] animate-pulse-slow" />
                    <div className="absolute top-[100px] right-[-100px] w-[500px] h-[500px] bg-secondary-500/10 rounded-full blur-[100px] animate-pulse-slow font-delay-2000" />
                </div>

                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-5xl md:text-8xl font-black tracking-tight leading-[0.9] md:leading-[1.05] mb-8 animate-slide-up">
                        Ship Stunning <br className="hidden md:block" />
                        <span className="bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] bg-clip-text text-transparent">Visuals.</span>
                        <br /> Every Single Day.
                    </h1>

                    <p className="text-lg md:text-xl text-dark-300 max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in delay-200">
                        Design quote cards, stats cards and SaaS journey posts in seconds. Built for founders who build in public.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-6 animate-fade-in delay-300">
                        <Link
                            to={session ? '/generator' : '/signup'}
                            className="w-full sm:w-auto px-10 py-5 rounded-2xl bg-white text-dark-950 font-black text-lg hover:bg-white/90 hoverScale transition-all shadow-glow-white"
                        >
                            Start Shipping Free →
                        </Link>
                        <button
                            onClick={() => document.getElementById('showcase')?.scrollIntoView({ behavior: 'smooth' })}
                            className="w-full sm:w-auto px-10 py-5 rounded-2xl bg-dark-900 border border-white/10 text-white font-bold text-lg hover:bg-dark-800 transition-all text-center"
                        >
                            View Examples
                        </button>
                    </div>

                    <div className="mt-10 flex items-center justify-center gap-2 text-dark-400 font-bold text-sm animate-fade-in delay-500">
                        <span>🚀 Join 500+ founders already shipping</span>
                    </div>

                    {/* ── Watch it in Action ── */}
                    {(() => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const videoRef = React.useRef<HTMLVideoElement>(null);
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const [playing, setPlaying] = React.useState(false);
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const [progress, setProgress] = React.useState(0);
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const [currentTime, setCurrentTime] = React.useState(0);
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const [duration, setDuration] = React.useState(0);
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const [volume, setVolume] = React.useState(1);
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const [muted, setMuted] = React.useState(false);
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const [showControls, setShowControls] = React.useState(true);
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        const hideTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

                        const fmt = (s: number) => {
                            const m = Math.floor(s / 60);
                            const sec = Math.floor(s % 60);
                            return `${m}:${sec.toString().padStart(2, '0')}`;
                        };
                        const togglePlay = () => {
                            const v = videoRef.current; if (!v) return;
                            if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
                        };
                        const onTimeUpdate = () => {
                            const v = videoRef.current; if (!v) return;
                            setCurrentTime(v.currentTime);
                            setProgress(v.duration ? (v.currentTime / v.duration) * 100 : 0);
                        };
                        const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
                            const v = videoRef.current; if (!v || !v.duration) return;
                            const val = Number(e.target.value);
                            v.currentTime = (val / 100) * v.duration;
                            setProgress(val);
                        };
                        const onVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                            const v = videoRef.current; if (!v) return;
                            const val = Number(e.target.value);
                            v.volume = val; setVolume(val); setMuted(val === 0);
                        };
                        const toggleMute = () => {
                            const v = videoRef.current; if (!v) return;
                            v.muted = !v.muted; setMuted(v.muted);
                        };
                        const toggleFullscreen = () => {
                            const wrapper = document.getElementById('video-player-wrapper');
                            if (!document.fullscreenElement) wrapper?.requestFullscreen();
                            else document.exitFullscreen();
                        };
                        const resetHideTimer = () => {
                            setShowControls(true);
                            clearTimeout(hideTimer.current);
                            hideTimer.current = setTimeout(() => { if (playing) setShowControls(false); }, 2500);
                        };

                        return (
                            <div id="showcase" className="mt-24 relative px-2 md:px-10 animate-slide-up delay-500">
                                {/* Label */}
                                <div className="flex items-center justify-center gap-3 mb-8">
                                    <div className="h-px w-12 bg-white/10" />
                                    <span className="text-xs font-black uppercase tracking-[0.25em] text-dark-400">Watch it in Action</span>
                                    <div className="h-px w-12 bg-white/10" />
                                </div>

                                {/* Player wrapper */}
                                <div
                                    id="video-player-wrapper"
                                    className="relative mx-auto max-w-[1100px] rounded-[32px] overflow-hidden shadow-[0_0_100px_rgba(124,58,237,0.25)] border border-white/10 bg-black group"
                                    onMouseMove={resetHideTimer}
                                    onMouseLeave={() => { if (playing) setShowControls(false); }}
                                >
                                    {/* === UPDATE ME: Replace /demo-video.mp4 with your real video === */}
                                    <video
                                        ref={videoRef}
                                        id="hero-demo-video"
                                        className="w-full aspect-video object-cover block cursor-pointer"
                                        src="/demo-video.mp4"
                                        preload="metadata"
                                        playsInline
                                        onTimeUpdate={onTimeUpdate}
                                        onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
                                        onEnded={() => setPlaying(false)}
                                        onClick={togglePlay}
                                    />

                                    {/* Big play overlay when paused */}
                                    {!playing && (
                                        <div
                                            className="absolute inset-0 flex flex-col items-center justify-center bg-[#0c1120]/75 backdrop-blur-[2px] cursor-pointer transition-opacity duration-300"
                                            onClick={togglePlay}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary-900/40 via-transparent to-secondary-900/30 pointer-events-none" />
                                            <div className="relative z-10 flex flex-col items-center gap-5">
                                                <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-white/10 border-2 border-white/20 backdrop-blur-xl flex items-center justify-center hover:scale-110 hover:bg-white/15 transition-all duration-500 shadow-[0_0_60px_rgba(124,58,237,0.5)]">
                                                    <svg className="w-8 h-8 md:w-11 md:h-11 fill-white ml-1.5" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                                </div>
                                                <div className="text-center">
                                                    <h2 className="text-xl md:text-3xl font-black text-white mb-1">See Share Visuals in Action</h2>
                                                    <p className="text-dark-400 text-sm font-medium">From idea to visual in under 5 seconds</p>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-dark-500 font-bold uppercase tracking-widest">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
                                                    2 min demo
                                                </div>
                                            </div>
                                            {/* Corner decorations */}
                                            <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-white/10 rounded-tl-xl" />
                                            <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-white/10 rounded-tr-xl" />
                                            <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-white/10 rounded-bl-xl" />
                                            <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-white/10 rounded-br-xl" />
                                        </div>
                                    )}

                                    {/* ── Controls bar ── */}
                                    <div className={`absolute bottom-0 left-0 right-0 transition-all duration-300 ${showControls || !playing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
                                        {/* Gradient fade */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />

                                        <div className="relative z-10 px-4 pb-4 pt-8 flex flex-col gap-2">
                                            {/* Progress scrubber */}
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-mono text-white/60 shrink-0 w-9 text-right">{fmt(currentTime)}</span>
                                                <div className="flex-1 relative group/scrub h-5 flex items-center">
                                                    <div className="absolute inset-y-0 flex items-center w-full">
                                                        {/* Track bg */}
                                                        <div className="w-full h-1 bg-white/15 rounded-full overflow-hidden">
                                                            {/* Filled */}
                                                            <div className="h-full bg-gradient-to-r from-primary-500 to-secondary-400 rounded-full transition-all" style={{ width: `${progress}%` }} />
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="range" min={0} max={100} step={0.1}
                                                        value={progress}
                                                        onChange={onScrub}
                                                        className="relative z-10 w-full h-1 opacity-0 cursor-pointer"
                                                    />
                                                </div>
                                                <span className="text-[10px] font-mono text-white/40 shrink-0 w-9">{duration ? fmt(duration) : '--:--'}</span>
                                            </div>

                                            {/* Buttons row */}
                                            <div className="flex items-center justify-between gap-3">
                                                {/* Left: Play/Pause + Volume */}
                                                <div className="flex items-center gap-3">
                                                    {/* Play/Pause */}
                                                    <button onClick={togglePlay} aria-label={playing ? "Pause video" : "Play video"} className="w-8 h-8 flex items-center justify-center hover:text-primary-400 text-white transition-colors">
                                                        {playing ? (
                                                            <svg className="w-5 h-5 fill-current" aria-hidden="true" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
                                                        ) : (
                                                            <svg className="w-5 h-5 fill-current ml-0.5" aria-hidden="true" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                                        )}
                                                    </button>

                                                    {/* Mute */}
                                                    <button onClick={toggleMute} aria-label={muted || volume === 0 ? "Unmute" : "Mute"} className="w-7 h-7 flex items-center justify-center hover:text-primary-400 text-white/80 transition-colors">
                                                        {muted || volume === 0 ? (
                                                            <svg className="w-4 h-4 fill-current" aria-hidden="true" viewBox="0 0 24 24"><path d="M16.5 12A4.5 4.5 0 0014 7.97v2.22l2.45 2.45A4.4 4.4 0 0016.5 12zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25A6.97 6.97 0 0112 19.58c-.38 0-.76-.04-1.12-.1v2.07A9 9 0 0012 21.58c1.74 0 3.41-.48 4.86-1.38l1.86 1.86 1.27-1.27L4.27 3zM12 3L9.91 5.09 12 7.18V3z" /></svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 fill-current" aria-hidden="true" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" /></svg>
                                                        )}
                                                    </button>

                                                    {/* Volume slider */}
                                                    <div className="w-16 hidden sm:flex items-center">
                                                        <input
                                                            type="range" min={0} max={1} step={0.05}
                                                            value={muted ? 0 : volume}
                                                            onChange={onVolumeChange}
                                                            className="w-full h-1 cursor-pointer accent-primary-500"
                                                        />
                                                    </div>

                                                    {/* Time */}
                                                    <span className="text-[11px] font-mono text-white/50 hidden sm:block">
                                                        {fmt(currentTime)} / {duration ? fmt(duration) : '--:--'}
                                                    </span>
                                                </div>

                                                {/* Right: Fullscreen */}
                                                <button onClick={toggleFullscreen} aria-label="Toggle Fullscreen" className="w-7 h-7 flex items-center justify-center hover:text-primary-400 text-white/80 transition-colors">
                                                    <svg className="w-4 h-4 fill-current" aria-hidden="true" viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Caption */}
                                <div className="mt-6 flex flex-col items-center gap-3">
                                    <p className="flex items-center gap-2 text-dark-300 text-sm md:text-base font-semibold">
                                        <span className="text-primary-400 animate-bounce-x inline-block">👉</span>
                                        Watch how Share Visuals creates shareable visuals in seconds
                                    </p>
                                    <div className="flex items-center gap-4 text-dark-600 text-xs font-bold uppercase tracking-widest">
                                        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />No sign-up needed</span>
                                        <span>·</span>
                                        <span>2 min</span>
                                        <span>·</span>
                                        <span>HD</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </section>

            {/* --- Testimonials --- */}
            <div className="section-divider" />
            <section className="py-24 px-0 bg-white/[0.01] overflow-hidden relative">
                <div className="max-w-7xl mx-auto mb-16 px-4 text-center">
                    <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">Trusted by Founders Shipping the Future</h2>
                    <p className="text-dark-400 text-xl font-medium max-w-2xl mx-auto">See why the world's most consistent builders choose Share Visuals to share their journey and grow their audience.</p>
                </div>

                <div className="relative overflow-hidden group/marquee pause-on-hover">
                    {/* Edge Masks */}
                    <div className="absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/80 to-transparent z-10 pointer-events-none hidden md:block" />
                    <div className="absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-[#0F172A] via-[#0F172A]/80 to-transparent z-10 pointer-events-none hidden md:block" />

                    <div className="animate-marquee flex gap-8 py-10 px-6 w-max">
                        {[...testimonials, ...testimonials, ...testimonials].map((t, i) => (
                            <a
                                key={i}
                                href={t.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-[300px] md:w-[360px] lg:w-[380px] flex-shrink-0 p-8 rounded-[32px] bg-dark-900/50 backdrop-blur-sm border border-white/5 relative group hover:-translate-y-2 transition-all duration-300 shadow-xl hover:border-primary-500/30 flex flex-col h-[320px]"
                            >
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 rounded-2xl bg-gradient-primary flex items-center justify-center font-black text-sm shadow-glow text-white shrink-0">
                                        {t.img}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h4 className="text-white font-bold truncate">{t.name}</h4>
                                        <p className="text-dark-500 text-[10px] font-black uppercase tracking-widest truncate">{t.handle}</p>
                                    </div>
                                    <div className="text-dark-500 group-hover:text-primary-400 transition-colors shrink-0">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="flex-1 min-h-0 overflow-hidden">
                                    <p className="text-dark-200 font-medium leading-relaxed italic line-clamp-5 text-sm md:text-base">
                                        "{t.text}"
                                    </p>
                                </div>
                                <div className="mt-auto pt-6 border-t border-white/5 flex justify-between items-center">
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4, 5].map(s => <span key={s} className="text-orange-500 text-[10px]">⭐</span>)}
                                    </div>
                                    <span className="text-primary-400 font-black text-[9px] uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity">View on X</span>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- PRO Feature: Batch Generation --- */}
            <div className="section-divider" />
            <section className="py-24 px-4 overflow-hidden relative">
                {/* Background Accent Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary-500/10 rounded-full blur-[120px] pointer-events-none" />
                <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-secondary-500/10 rounded-full blur-[100px] pointer-events-none" />

                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                        {/* RIGHT: Video side */}
                        <div className="order-2 lg:order-2 relative group">
                            {(() => {
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const videoRef = React.useRef<HTMLVideoElement>(null);
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const [playing, setPlaying] = React.useState(false);
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const [progress, setProgress] = React.useState(0);
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const [currentTime, setCurrentTime] = React.useState(0);
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const [duration, setDuration] = React.useState(0);
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const [volume, setVolume] = React.useState(1);
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const [muted, setMuted] = React.useState(false);
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const [showControls, setShowControls] = React.useState(true);
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                const hideTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

                                const fmt = (s: number) => {
                                    const m = Math.floor(s / 60);
                                    const sec = Math.floor(s % 60);
                                    return `${m}:${sec.toString().padStart(2, '0')}`;
                                };
                                const togglePlay = () => {
                                    const v = videoRef.current; if (!v) return;
                                    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
                                };
                                const onTimeUpdate = () => {
                                    const v = videoRef.current; if (!v) return;
                                    setCurrentTime(v.currentTime);
                                    setProgress(v.duration ? (v.currentTime / v.duration) * 100 : 0);
                                };
                                const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
                                    const v = videoRef.current; if (!v || !v.duration) return;
                                    const val = Number(e.target.value);
                                    v.currentTime = (val / 100) * v.duration;
                                    setProgress(val);
                                };
                                const onVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                    const v = videoRef.current; if (!v) return;
                                    const val = Number(e.target.value);
                                    v.volume = val; setVolume(val); setMuted(val === 0);
                                };
                                const toggleMute = () => {
                                    const v = videoRef.current; if (!v) return;
                                    v.muted = !v.muted; setMuted(v.muted);
                                };
                                const toggleFullscreen = () => {
                                    const wrapper = document.getElementById('pro-video-player-wrapper');
                                    if (!document.fullscreenElement) wrapper?.requestFullscreen();
                                    else document.exitFullscreen();
                                };
                                const resetHideTimer = () => {
                                    setShowControls(true);
                                    clearTimeout(hideTimer.current);
                                    hideTimer.current = setTimeout(() => { if (playing) setShowControls(false); }, 2500);
                                };

                                return (
                                    <div className="relative group/player">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-[32px] blur opacity-25 group-hover/player:opacity-40 transition duration-1000 group-hover/player:duration-200" />
                                        <div
                                            id="pro-video-player-wrapper"
                                            className="relative rounded-[32px] overflow-hidden bg-dark-950 aspect-video ring-1 ring-white/10 shadow-2xl group"
                                            onMouseMove={resetHideTimer}
                                            onMouseLeave={() => { if (playing) setShowControls(false); }}
                                        >
                                            <video
                                                ref={videoRef}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                className="w-full h-full object-cover cursor-pointer"
                                                onTimeUpdate={onTimeUpdate}
                                                onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
                                                onClick={togglePlay}
                                            >
                                                <source src="/pro-video-demo.mp4" type="video/mp4" />
                                            </video>

                                            {/* Overlay Caption (Hidden when playing and controls hide) */}
                                            <div className={`absolute bottom-16 left-6 right-6 transition-all duration-300 ${showControls || !playing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                                                <div className="bg-black/60 backdrop-blur-md border border-white/10 px-4 py-3 rounded-2xl inline-flex items-center gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                                    <p className="text-white text-sm font-bold">Generate 365 visuals in seconds</p>
                                                </div>
                                            </div>

                                            {/* ── Controls bar ── */}
                                            <div className={`absolute bottom-0 left-0 right-0 transition-all duration-300 z-20 ${showControls || !playing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
                                                <div className="relative z-10 px-4 pb-4 pt-8 flex flex-col gap-2">
                                                    {/* Progress scrubber */}
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-mono text-white/60 shrink-0 w-9 text-right">{fmt(currentTime)}</span>
                                                        <div className="flex-1 relative group/scrub h-5 flex items-center">
                                                            <div className="absolute inset-y-0 flex items-center w-full">
                                                                <div className="w-full h-1 bg-white/15 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-gradient-to-r from-primary-500 to-secondary-400 rounded-full transition-all" style={{ width: `${progress}%` }} />
                                                                </div>
                                                            </div>
                                                            <input
                                                                type="range" min={0} max={100} step={0.1}
                                                                value={progress}
                                                                onChange={onScrub}
                                                                className="relative z-10 w-full h-1 opacity-0 cursor-pointer"
                                                            />
                                                        </div>
                                                        <span className="text-[10px] font-mono text-white/40 shrink-0 w-9">{duration ? fmt(duration) : '--:--'}</span>
                                                    </div>

                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-3">
                                                            <button onClick={togglePlay} aria-label={playing ? "Pause video" : "Play video"} className="w-8 h-8 flex items-center justify-center hover:text-primary-400 text-white transition-colors">
                                                                {playing ? (
                                                                    <svg className="w-5 h-5 fill-current" aria-hidden="true" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
                                                                ) : (
                                                                    <svg className="w-5 h-5 fill-current ml-0.5" aria-hidden="true" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                                                )}
                                                            </button>
                                                            <button onClick={toggleMute} aria-label={muted || volume === 0 ? "Unmute" : "Mute"} className="w-7 h-7 flex items-center justify-center hover:text-primary-400 text-white/80 transition-colors">
                                                                {muted || volume === 0 ? (
                                                                    <svg className="w-4 h-4 fill-current" aria-hidden="true" viewBox="0 0 24 24"><path d="M16.5 12A4.5 4.5 0 0014 7.97v2.22l2.45 2.45A4.4 4.4 0 0016.5 12zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25A6.97 6.97 0 0112 19.58c-.38 0-.76-.04-1.12-.1v2.07A9 9 0 0012 21.58c1.74 0 3.41-.48 4.86-1.38l1.86 1.86 1.27-1.27L4.27 3zM12 3L9.91 5.09 12 7.18V3z" /></svg>
                                                                ) : (
                                                                    <svg className="w-4 h-4 fill-current" aria-hidden="true" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" /></svg>
                                                                )}
                                                            </button>
                                                            <div className="w-16 hidden sm:flex items-center">
                                                                <input
                                                                    type="range" min={0} max={1} step={0.05}
                                                                    value={muted ? 0 : volume}
                                                                    onChange={onVolumeChange}
                                                                    className="w-full h-1 cursor-pointer accent-primary-500"
                                                                />
                                                            </div>
                                                        </div>
                                                        <button onClick={toggleFullscreen} aria-label="Toggle Fullscreen" className="w-7 h-7 flex items-center justify-center hover:text-primary-400 text-white/80 transition-colors">
                                                            <svg className="w-4 h-4 fill-current" aria-hidden="true" viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* LEFT: Content side */}
                        <div className="order-1 lg:order-1 space-y-8">
                            <div className="space-y-4">
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-[10px] font-black uppercase tracking-[0.2em]">
                                    🔒 PRO FEATURE
                                </span>
                                <h2 className="text-4xl md:text-6xl font-black text-white leading-tight mb-4">
                                    Generate 365 <br />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">
                                        Visuals
                                    </span> in Seconds
                                </h2>
                                <p className="text-dark-400 text-xl font-medium leading-relaxed max-w-xl">
                                    Upload a CSV and generate 1 year of content instantly. Perfect for creators, agencies, and social media growth.
                                </p>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-6">
                                <ul className="space-y-4">
                                    {[
                                        'create 365 visuals instantly',
                                        'save hours of manual work',
                                        'perfect for content scheduling',
                                        'consistent branding at scale',
                                    ].map((benefit, i) => (
                                        <li key={i} className="flex items-center gap-3 text-dark-200 font-bold text-sm">
                                            <div className="shrink-0 w-5 h-5 rounded-full bg-primary-500/20 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* How it Works - Mini Steps */}
                            <div className="grid grid-cols-3 gap-4 p-6 rounded-3xl bg-white/[0.02] border border-white/5">
                                {[
                                    { t: 'Upload CSV', d: 'Add one quote per line.' },
                                    { t: 'Choose Template', d: 'Select your visual style.' },
                                    { t: 'Generate & Export', d: 'Get all visuals at once.' }
                                ].map((step, i) => (
                                    <div key={i} className="space-y-1">
                                        <h3 className="text-white text-[11px] font-black uppercase tracking-wider">{step.t}</h3>
                                        <p className="text-dark-500 text-[10px] font-medium leading-tight">{step.d}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-4">
                                <div className="flex flex-col sm:flex-row items-center gap-4">
                                    <Link
                                        to="/upgrade"
                                        className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-primary-600 hover:bg-primary-500 text-white font-black text-center transition-all shadow-glow active:scale-95"
                                    >
                                        Upgrade to unlock batch generation
                                    </Link>
                                    <Link
                                        to="#pricing"
                                        className="text-dark-400 hover:text-white font-bold text-sm transition-colors"
                                    >
                                        See plans →
                                    </Link>
                                </div>
                                <div className="flex items-center gap-4 text-dark-500">
                                    <a
                                        href="/sample-quotes.csv"
                                        download
                                        className="text-[10px] font-medium flex items-center gap-1.5 cursor-pointer hover:text-white transition-colors"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                        Download sample CSV
                                    </a>
                                    <span className="text-dark-800">|</span>
                                    <p className="text-[10px] font-medium flex items-center gap-1.5">
                                        ✨ No technical skills required
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Use Cases --- */}
            <div className="section-divider" />
            <section className="py-20 px-4 relative bg-[#0f1012]">
                <div className="max-w-7xl mx-auto text-center">
                    <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">What You Can Create</h2>
                    <p className="text-dark-400 text-xl font-medium max-w-2xl mx-auto mb-16">Share Visuals is perfect for every stage of your journey.</p>

                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        {['Build in public posts', 'First SaaS sale posts', 'Quote cards', 'Growth stats', 'Milestone updates'].map((useCase, i) => (
                            <div key={i} className="p-6 rounded-3xl bg-[#111827] border border-white/5 hover:border-primary-500/30 transition-all group flex flex-col items-center justify-center text-center shadow-lg hover:-translate-y-1 h-32">
                                <span className="text-white font-bold text-sm group-hover:text-primary-400 transition-colors">{useCase}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Stats --- */}
            <div className="section-divider" />
            <section className="py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-primary-500/5 via-transparent to-transparent pointer-events-none" />

                {/* Background Decorative Blobs */}
                <div className="absolute top-1/2 left-0 -translate-y-1/2 w-80 h-80 bg-primary-500/10 blur-[120px] rounded-full animate-blob pointer-events-none" />
                <div className="absolute top-1/2 right-0 -translate-y-1/2 w-80 h-80 bg-secondary-500/10 blur-[120px] rounded-full animate-blob animation-delay-2000 pointer-events-none" />

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { label: 'Images Shipped', value: 100, suffix: '+', icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>, color: 'text-primary-400' },
                            { label: 'Active Creators', value: 100, suffix: '+', icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>, color: 'text-secondary-400' },
                            { label: 'Satisfaction', value: 99, suffix: '%', icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.383-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>, color: 'text-orange-400' },
                            { label: 'Generation Speed', value: 0, suffix: 'Instant', isSpecial: true, icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>, color: 'text-success-400' }
                        ].map((s, i) => (
                            <div key={i} className="group relative p-8 rounded-[32px] bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] hover:border-white/10 transition-all duration-500 hover-glow overflow-hidden">
                                {/* Decorative Gradient */}
                                <div className={`absolute top-0 right-0 w-24 h-24 bg-current opacity-0 group-hover:opacity-5 blur-[40px] transition-opacity -mr-8 -mt-8 ${s.color}`} />

                                <div className="relative z-10">
                                    <div className={`w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 ${s.color}`}>
                                        {s.icon}
                                    </div>

                                    <div className="text-3xl md:text-4xl font-black text-white mb-2 tracking-tight">
                                        {s.isSpecial ? (
                                            <span className="animate-pulse">{s.suffix}</span>
                                        ) : (
                                            <CountUp end={s.value} suffix={s.suffix} />
                                        )}
                                    </div>

                                    <div className="text-sm font-bold text-dark-500 uppercase tracking-widest leading-none">
                                        {s.label}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Example Gallery Slider --- */}
            <section className="py-20 overflow-hidden relative">
                <div className="text-center mb-20 px-4 text-white">
                    <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">Create Scroll-Stopping Visuals.</h2>
                    <p className="text-dark-400 text-xl font-medium max-w-2xl mx-auto">Actual templates generated in under 5 seconds.</p>
                </div>

                {/* === UPDATE ME: Add more images to /public/templates-demo/ and update the arrays below === */}
                <div className="space-y-8 pause-on-hover">
                    {/* First Row: Left to Right */}
                    <div className="flex overflow-hidden">
                        <div className="animate-marquee-reverse flex gap-6">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 1, 2, 3, 4, 5, 6, 7, 8].map((n, i) => (
                                <div key={`r1-${i}`} className="w-[280px] md:w-[400px] flex-shrink-0 rounded-3xl overflow-hidden border border-white/10 hover:border-primary-500/30 shadow-2xl hover:shadow-[0_0_40px_rgba(124,58,237,0.15)] transition-all duration-500 group">
                                    <img
                                        src={`/templates-demo/template-${n}.png`}
                                        alt={`Share Visuals Template ${n}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Second Row: Right to Left */}
                    <div className="flex overflow-hidden">
                        <div className="animate-marquee flex gap-6">
                            {[9, 10, 11, 12, 13, 14, 15, 16, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27].map((n, i) => (
                                <div key={`r2-${i}`} className="w-[280px] md:w-[400px] flex-shrink-0 rounded-3xl overflow-hidden border border-white/10 hover:border-secondary-500/30 shadow-2xl hover:shadow-[0_0_40px_rgba(6,182,212,0.15)] transition-all duration-500 group">
                                    <img
                                        src={`/templates-demo/template-${n}.png`}
                                        alt={`Share Visuals Template ${n}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Features --- */}
            <div className="section-divider" />
            <section id="features" className="py-20 px-4 relative bg-white/[0.01]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">Everything You Need to Design Faster.</h2>
                        <p className="text-xl text-dark-400 font-medium max-w-2xl mx-auto">Everything you need to create professional assets.</p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((f, i) => {
                            const accents = [
                                { border: 'border-primary-500/20', icon: 'bg-primary-500/10 text-primary-400', glow: 'hover:shadow-[0_0_40px_rgba(124,58,237,0.25)]', hover: 'hover:border-primary-500/40' },
                                { border: 'border-secondary-500/20', icon: 'bg-secondary-500/10 text-secondary-400', glow: 'hover:shadow-[0_0_40px_rgba(6,182,212,0.2)]', hover: 'hover:border-secondary-500/40' },
                                { border: 'border-orange-500/20', icon: 'bg-orange-500/10 text-orange-400', glow: 'hover:shadow-[0_0_40px_rgba(249,115,22,0.2)]', hover: 'hover:border-orange-500/40' },
                                { border: 'border-primary-500/20', icon: 'bg-primary-500/10 text-primary-400', glow: 'hover:shadow-[0_0_40px_rgba(124,58,237,0.25)]', hover: 'hover:border-primary-500/40' },
                                { border: 'border-secondary-500/20', icon: 'bg-secondary-500/10 text-secondary-400', glow: 'hover:shadow-[0_0_40px_rgba(6,182,212,0.2)]', hover: 'hover:border-secondary-500/40' },
                                { border: 'border-success-500/20', icon: 'bg-success-500/10 text-success-400', glow: 'hover:shadow-[0_0_40px_rgba(34,197,94,0.2)]', hover: 'hover:border-success-500/40' },
                            ];
                            const a = accents[i % accents.length];
                            return (
                                <div key={i} className={`group p-10 rounded-[40px] bg-[#111827] border ${a.border} ${a.hover} ${a.glow} transition-all duration-300 relative overflow-hidden hover:-translate-y-1`}>
                                    {/* Animated hover gradient overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className={`w-14 h-14 rounded-2xl ${a.icon} flex items-center justify-center mb-8 relative z-10 group-hover:scale-110 transition-transform duration-300`}>
                                        {f.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-3 relative z-10">{f.title}</h3>
                                    <p className="text-dark-400 leading-relaxed font-medium relative z-10 text-sm">{f.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* --- Comparison Table --- */}
            <div className="section-divider" />
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16 px-4">
                        <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">Why Creators Choose Share Visuals.</h2>
                        <p className="text-dark-400 text-xl font-medium max-w-2xl mx-auto">Engineered for speed, designed for impact.</p>
                    </div>

                    <div className="space-y-3">
                        {/* Header — hidden on mobile, shown on md+ */}
                        <div className="hidden md:grid grid-cols-12 px-6 py-5 rounded-2xl bg-white/[0.02] border border-white/5 mb-1">
                            <div className="col-span-6 text-xs font-black uppercase tracking-[0.2em] text-dark-400">Capability</div>
                            <div className="col-span-3 text-center">
                                <span className="text-xs font-black uppercase tracking-[0.15em] text-dark-500">Canva</span>
                            </div>
                            <div className="col-span-3 text-center">
                                <span className="text-xs font-black uppercase tracking-[0.15em] text-primary-400 bg-primary-500/10 px-3 py-1 rounded-lg">Share Visuals ✦</span>
                            </div>
                        </div>

                        {[
                            { f: 'Build-in-public templates', legacy: '❌', ig: '✅', unique: true },
                            { f: 'Bulk visual generation', legacy: '❌', ig: '✅', unique: true },
                            { f: 'Founder milestones', legacy: '❌', ig: '✅', unique: true },
                            { f: 'SaaS update visuals', legacy: '❌', ig: '✅', unique: true },
                        ].map((row, i) => (
                            <div key={i} className={`rounded-2xl border transition-all duration-300 group overflow-hidden ${row.unique ? 'bg-primary-500/5 border-primary-500/20 hover:border-primary-500/40' : 'bg-[#0c0c0e] border-white/5 hover:border-white/10'}`}>
                                {/* Mobile layout */}
                                <div className="flex flex-col md:hidden px-5 py-4 gap-3">
                                    {/* Badge + Feature name stacked */}
                                    <div className="flex flex-col gap-1.5">
                                        {row.unique && (
                                            <span className="self-start text-[9px] font-black uppercase tracking-widest bg-primary-500 text-white px-2 py-0.5 rounded-full">Only Us</span>
                                        )}
                                        <span className="text-sm font-bold text-white">{row.f}</span>
                                    </div>
                                    {/* Values side by side */}
                                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                                        <div className="bg-white/5 rounded-xl px-3 py-2 text-center">
                                            <p className="text-[9px] uppercase font-black tracking-widest text-white/40 mb-1">Others</p>
                                            <span className={`text-xs font-semibold ${row.unique ? 'text-red-400' : 'text-white/60'}`}>{row.legacy}</span>
                                        </div>
                                        <div className="bg-primary-500/10 border border-primary-500/20 rounded-xl px-3 py-2 text-center">
                                            <p className="text-[9px] uppercase font-black tracking-widest text-primary-400 mb-1">Share Visuals</p>
                                            <span className={`text-xs font-black ${row.unique ? 'text-primary-300' : 'text-primary-400'}`}>{row.ig}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Desktop layout */}
                                <div className="hidden md:grid grid-cols-12 items-center px-6 py-5">
                                    <div className="col-span-6 flex flex-col gap-1">
                                        {row.unique && (
                                            <span className="self-start text-[9px] font-black uppercase tracking-widest bg-primary-500 text-white px-2 py-0.5 rounded-full">Only Us</span>
                                        )}
                                        <span className="text-base font-bold text-white transition-colors">{row.f}</span>
                                    </div>
                                    <div className="col-span-3 text-center">
                                        <span className={`font-medium text-sm ${row.unique ? 'text-red-400' : 'text-white'}`}>{row.legacy}</span>
                                    </div>
                                    <div className="col-span-3 text-center relative">
                                        <div className="absolute inset-0 bg-primary-500/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <span className={`relative z-10 font-black text-sm tracking-tight ${row.unique ? 'text-primary-300' : 'text-primary-400'}`}>
                                            {row.ig}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quick Insight */}
                    <p className="mt-12 text-center text-dark-500 font-bold text-sm">
                        Stop wasting hours. Start shipping assets.
                    </p>
                </div>
            </section>

            {/* --- How it Works --- */}
            <div className="section-divider" />
            <section id="how-it-works" className="py-20 px-4">
                <div className="max-w-7xl mx-auto text-center mb-20">
                    <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">Create in 3 Simple Steps</h2>
                    <p className="text-dark-400 text-xl font-medium max-w-2xl mx-auto">Simple, elegant, and impossibly fast.</p>
                </div>

                <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-16 md:gap-8">
                    {steps.map((s, i) => (
                        <div key={i} className="relative group text-center px-8 py-12 rounded-[40px] bg-white/[0.02] border border-white/5 hover:border-primary-500/20 transition-all duration-500">
                            <div className="text-9xl font-black text-white/[0.06] absolute -top-8 left-1/2 -translate-x-1/2 select-none group-hover:text-primary-500/10 transition-colors pointer-events-none">
                                {s.num}
                            </div>
                            <div className="relative z-10">
                                <div className="w-12 h-12 rounded-2xl bg-primary-600 flex items-center justify-center mx-auto mb-8 shadow-glow font-black text-white">
                                    {s.num}
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-4">{s.title}</h3>
                                <p className="text-dark-400 font-medium leading-relaxed">{s.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* --- FAQ Section --- */}
            <div className="section-divider" />
            <section className="py-20 px-4 bg-white/[0.01]">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-20 text-white">
                        <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">Common Questions</h2>
                        <p className="text-dark-400 text-xl font-medium max-w-2xl mx-auto">Everything you need to know about sharing with Share Visuals.</p>
                    </div>

                    <div className="space-y-3">
                        {[
                            { q: "Can I use Share Visuals for commercial projects?", a: "Yes! All plans including free allow commercial use of generated images. You own everything you create." },
                            { q: "How many images can I generate for free?", a: "Free plan includes 10 images per day forever — no credit card needed. Upgrade anytime for unlimited generations." },
                            { q: "Is there a free trial?", a: "Yes! Our free plan is available forever — no trial period, no card required. Start shipping immediately with 10 images/day." },
                            { q: "What image sizes does Share Visuals support?", a: "Instagram Square (1080×1080), Story (1080×1920), Twitter/X (1200×675), and WhatsApp (800×800). More sizes coming soon." },
                            { q: "Can I remove the watermark?", a: "Yes! Creator plan and above removes all watermarks from your exported images." },
                            { q: "Is there a lifetime deal available?", a: "Yes! We offer a Founder's Lifetime Deal for a one-time payment — pay once, use forever. Limited spots available." },
                            { q: "What payment methods are accepted?", a: "All major credit and debit cards are accepted securely via DodoPayments. Your payment info is never stored on our servers." },
                            { q: "Do you offer refunds?", a: "Absolutely. We offer a 7-day money-back guarantee on all paid plans, no questions asked." },
                            { q: "Can I cancel my subscription anytime?", a: "Yes, cancel anytime. You keep full access until the end of your billing period." },
                        ].map((faq, i) => (
                            <details key={i} className="group p-7 rounded-3xl bg-[#111827] border border-white/5 hover:border-white/10 open:border-primary-500/20 open:bg-primary-500/[0.03] transition-all duration-200">
                                <summary className="flex items-center justify-between cursor-pointer list-none font-bold text-base text-white gap-4">
                                    <span>{faq.q}</span>
                                    <span className="text-primary-500 shrink-0 group-open:rotate-180 transition-transform duration-300">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </span>
                                </summary>
                                <p className="mt-4 text-dark-400 font-medium leading-relaxed text-sm border-t border-white/5 pt-4">
                                    {faq.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Pricing --- */}
            <div className="section-divider" />
            <section id="pricing" className="py-20 px-4 relative">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-10">
                        <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">Pricing for Every Stage of Your Journey</h2>
                        <p className="text-dark-400 text-xl font-medium max-w-2xl mx-auto">Simple plans that scale with your creativity.</p>
                    </div>

                    {/* Pricing Toggle */}
                    <div className="flex justify-center mb-16">
                        <div className="bg-dark-900 border border-white/5 p-1.5 rounded-2xl flex items-center shadow-2xl backdrop-blur-sm">
                            <button
                                onClick={() => setIsLifetime(false)}
                                className={`px-8 py-3 rounded-xl text-sm font-bold transition-all ${!isLifetime ? 'bg-white text-dark-950 shadow-glow-white' : 'text-dark-400 hover:text-white'}`}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setIsLifetime(true)}
                                className={`px-8 py-3 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${isLifetime ? 'bg-primary-500 text-white shadow-glow' : 'text-dark-400 hover:text-white'}`}
                            >
                                Lifetime
                                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-black ${isLifetime ? 'bg-white/20 border-white/30 text-white' : 'bg-primary-500 text-white border-primary-400 shadow-glow'}`}>
                                    -50%
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {/* Free Plan */}
                        <div className="group p-8 rounded-[40px] bg-dark-900 border border-white/5 hover:border-white/10 transition-all duration-300 flex flex-col">
                            <div className="mb-8">
                                <h3 className="text-xl font-bold text-white mb-2">Free</h3>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-4xl font-black text-white">$0</span>
                                    <span className="text-dark-500 font-bold uppercase tracking-widest text-[10px]">/month</span>
                                </div>
                            </div>
                            <ul className="space-y-4 mb-10 flex-1 text-sm">
                                {[
                                    { text: '10 images per day', icon: 'check' },
                                    { text: 'All fonts & colors', icon: 'check' },
                                    { text: 'Download as PNG', icon: 'check' },
                                    { text: 'Watermark on images', icon: 'cross' },
                                ].map((item, i) => (
                                    <li key={i} className={`flex items-center gap-3 font-medium ${item.icon === 'cross' ? 'text-dark-500' : 'text-dark-300'}`}>
                                        {item.icon === 'check' ? (
                                            <svg className="w-5 h-5 text-success-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5 text-danger-500/70 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        )}
                                        {item.text}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/signup" className="w-full py-4 rounded-2xl bg-dark-800 text-white font-black text-center text-sm hover:bg-dark-700 transition-all active:scale-95 border border-white/5">
                                Get Started Free
                            </Link>
                        </div>

                        {/* Creator Plan */}
                        <div className="group p-8 rounded-[40px] bg-dark-900 border border-white/10 hover:border-primary-500/30 transition-all duration-300 flex flex-col relative overflow-hidden ring-1 ring-white/5">
                            <div className="mb-8">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-xl font-bold text-white">Creator</h3>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 animate-pulse">🔥 Launch Price</span>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-xl text-dark-500 line-through font-bold"> $12 </span>
                                    <span className="text-4xl font-black text-white">$5</span>
                                    <span className="text-dark-500 font-bold uppercase tracking-widest text-[10px]">/month</span>
                                </div>
                            </div>
                            <ul className="space-y-4 mb-10 flex-1 text-sm">
                                {[
                                    'Unlimited generations',
                                    'No watermark',
                                    'All premium fonts',
                                    'Save to dashboard',
                                    'Priority Support',
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-dark-100 font-medium">
                                        <svg className="w-5 h-5 text-primary-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/upgrade" className="w-full py-4 rounded-2xl border-2 border-primary-500/50 text-white font-black text-center text-sm hover:bg-primary-500 transition-all active:scale-95 shadow-glow/20">
                                Go Creator
                            </Link>
                        </div>

                        {/* Pro or Lifetime Plan */}
                        {!isLifetime ? (
                            <div className="group p-8 rounded-[40px] bg-dark-900 border border-primary-500/30 hover:border-primary-500/60 transition-all duration-300 flex flex-col relative overflow-hidden ring-1 ring-primary-500/20 shadow-glow">
                                <div className="absolute top-0 right-0 bg-primary-500 text-white px-5 py-2 rounded-bl-3xl text-[10px] font-black uppercase tracking-widest shadow-xl z-10">
                                    Most Popular
                                </div>
                                <div className="mb-8">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-xl font-bold text-primary-400">Pro</h3>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 animate-pulse">🔥 Launch Price</span>
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-xl text-dark-500 line-through font-bold"> $25 </span>
                                        <span className="text-4xl font-black text-white">$15</span>
                                        <span className="text-dark-500 font-bold uppercase tracking-widest text-[10px]">/month</span>
                                    </div>
                                </div>
                                <ul className="space-y-4 mb-10 flex-1 text-sm">
                                    {[
                                        'Everything in Creator',
                                        'Batch CSV generation',
                                        'Download all as ZIP',
                                        'Brand Kits (Coming)',
                                        'Custom Logos',
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-center gap-3 text-white font-medium">
                                            <svg className="w-5 h-5 text-primary-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/upgrade" className="w-full py-4 rounded-2xl bg-primary-500 text-white font-black text-center text-sm hover:bg-primary-600 transition-all active:scale-95 shadow-glow">
                                    Get Pro Now
                                </Link>
                            </div>
                        ) : (
                            <div className="group p-8 rounded-[40px] bg-dark-950 border-2 border-primary-500/50 hover:border-primary-500 transition-all duration-500 flex flex-col relative overflow-hidden shadow-[0_0_60px_rgba(124,58,237,0.3)] scale-[1.02] z-10">
                                <div className="absolute top-0 right-0 bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-2 rounded-bl-3xl text-[10px] font-black uppercase tracking-widest shadow-xl">
                                    Top Value
                                </div>
                                <div className="mb-8">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-xl font-bold text-white">Lifetime Access</h3>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 animate-pulse">🔥 Founder's Deal</span>
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-2xl text-dark-500 line-through font-bold"> $79 </span>
                                        <span className="text-5xl font-black text-white">$39</span>
                                        <span className="text-dark-500 font-bold uppercase tracking-widest text-[10px]">One-time</span>
                                    </div>
                                </div>
                                <ul className="space-y-4 mb-10 flex-1 text-sm">
                                    {[
                                        'Everything in Pro',
                                        'Pay once, use forever',
                                        'All future updates',
                                        'Founder Badge',
                                        'Private Community',
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-center gap-3 text-white font-semibold">
                                            <svg className="w-5 h-5 text-secondary-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/upgrade" className="w-full py-5 rounded-2xl bg-white text-dark-950 font-black text-center text-lg hover:bg-white/90 hoverScale animate-pulse-white transition-all shadow-glow-white">
                                    Claim Lifetime Deal
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Trust Signals */}
                    <div className="mt-20 flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-80 animate-fade-in">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-success-500/10 flex items-center justify-center">
                                <svg className="w-5 h-5 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <span className="text-white/80 font-bold text-sm">🔒 Secure payment by DodoPayments</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center">
                                <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <span className="text-white/80 font-bold text-sm">✅ 7-day money back guarantee</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex -space-x-2">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="w-8 h-8 rounded-full bg-dark-700 border-2 border-dark-900 flex items-center justify-center overflow-hidden">
                                        <div className="w-full h-full bg-gradient-primary opacity-50" />
                                    </div>
                                ))}
                            </div>
                            <span className="text-white/80 font-bold text-sm">⭐ Trusted by 100+ creators</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Final CTA --- */}
            <section className="py-32 px-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-[#050507]" />
                {/* Dynamic Background Elements */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary-600/10 rounded-full blur-[120px] animate-pulse-slow pointer-events-none" />

                <div className="max-w-5xl mx-auto relative">
                    <div className="group relative p-10 md:p-20 rounded-[48px] bg-dark-900 border border-white/10 overflow-hidden shadow-2xl">
                        {/* Interactive Glow Effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-transparent to-secondary-500/5 opacity-50 group-hover:opacity-100 transition-opacity duration-700" />

                        <div className="relative z-10 text-center">
                            <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-6 text-white leading-tight">
                                Ready to Ship Stunning <span className="bg-gradient-primary bg-clip-text text-transparent">Visuals?</span>
                            </h2>
                            <p className="text-lg text-dark-400 mb-10 max-w-xl mx-auto font-medium">
                                Join 500+ founders and start building your brand today.
                            </p>

                            <div className="flex justify-center">
                                <Link
                                    to={session ? '/generator' : '/signup'}
                                    className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl bg-white text-dark-950 text-lg font-black hoverScale transition-all shadow-glow-white overflow-hidden"
                                >
                                    Get Started for Free →
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Footer --- */}
            <footer className="py-20 px-4 border-t border-white/5 relative z-10">
                <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-5 gap-12 mb-20">
                    <div className="col-span-2">
                        <Link to="/" className="flex items-center gap-3 mb-6">
                            <img
                                src="/logo.png"
                                alt="Share Visuals"
                                className="h-10 w-auto"
                            />
                            <span className="text-2xl font-black text-white">Share Visuals</span>
                        </Link>
                        <p className="text-dark-400 max-w-xs leading-relaxed font-medium">
                            Ship stunning visuals. Fast.
                        </p>
                    </div>
                    <div>
                        <h3 className="text-white font-bold mb-6">Product</h3>
                        <ul className="space-y-4">
                            <li><Link to="/generator" className="text-dark-400 hover:text-white transition-colors text-sm font-medium">Visual Generator</Link></li>
                            <li><Link to="/upgrade" className="text-dark-400 hover:text-white transition-colors text-sm font-medium">Pricing</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-white font-bold mb-6">Legal</h3>
                        <ul className="space-y-4">
                            <li><Link to="/privacy" className="text-dark-400 hover:text-white transition-colors text-sm font-medium">Privacy Policy</Link></li>
                            <li><Link to="/terms" className="text-dark-400 hover:text-white transition-colors text-sm font-medium">Terms of Service</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-white font-bold mb-6">Connect</h3>
                        <div className="flex gap-5">
                            <a href="https://x.com/Nagaraj_Dev143" target="_blank" rel="noopener noreferrer" className="text-dark-400 hover:text-white transition-colors" title="Follow on X">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                            </a>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto pt-10 border-t border-white/5 text-center">
                    <div className="text-dark-500 text-sm font-medium">
                        © {new Date().getFullYear()} Share Visuals. All rights reserved. Built with ❤️ for creators.
                    </div>
                </div>
            </footer>
        </div >
    );
};

export default Landing;
